import { useState, useRef, useEffect } from 'react';
import { ChevronDown, Search, Loader2, X } from 'lucide-react';
import { Floating } from './Floating';

interface Props {
  options: string[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  loading?: boolean;
  disabled?: boolean;
  error?: string;
  className?: string;
}

export function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = 'Select...',
  loading = false,
  disabled = false,
  className = '',
}: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const filtered = query.trim()
    ? options.filter(o => o.toLowerCase().includes(query.toLowerCase()))
    : options;

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 10);
    else setQuery('');
  }, [open]);

  const handleSelect = (opt: string) => {
    onChange(opt);
    setOpen(false);
    setQuery('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
    setOpen(false);
  };

  const isDisabled = disabled || loading;

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Trigger */}
      <button
        type="button"
        disabled={isDisabled}
        onClick={() => !isDisabled && setOpen(v => !v)}
        className={`
          w-full h-10 px-3 rounded-lg border text-sm text-left flex items-center justify-between gap-2
          focus:outline-none focus:ring-2 focus:ring-[#6366F1] transition-colors
          ${isDisabled
            ? 'border-gray-100 bg-gray-50 text-gray-400 cursor-not-allowed'
            : 'border-gray-200 bg-white text-[#1E293B] cursor-pointer hover:border-[#6366F1]/40'}
          ${open ? 'ring-2 ring-[#6366F1] border-[#6366F1]/40' : ''}
        `}
      >
        <span className={`truncate flex-1 ${!value ? 'text-gray-400' : 'text-[#1E293B]'}`}>
          {value || placeholder}
        </span>
        <span className="flex items-center gap-1 flex-shrink-0">
          {loading && <Loader2 className="h-3.5 w-3.5 animate-spin text-[#6366F1]" />}
          {!loading && value && (
            <span
              role="button"
              onClick={handleClear}
              className="p-0.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600"
            >
              <X className="h-3 w-3" />
            </span>
          )}
          <ChevronDown className={`h-3.5 w-3.5 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
        </span>
      </button>

      {/* Dropdown — portalled so it escapes any overflow-hidden ancestor. */}
      <Floating
        open={open}
        anchorRef={containerRef}
        onClose={() => setOpen(false)}
        className="bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden"
        style={{ maxHeight: 260, display: 'flex', flexDirection: 'column' }}
      >
        {/* Search input */}
        <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100 flex-shrink-0">
          <Search className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            className="flex-1 text-sm bg-transparent outline-none text-[#1E293B] placeholder:text-gray-400"
            placeholder="Type to search..."
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && filtered.length === 1) handleSelect(filtered[0]);
            }}
          />
          {query && (
            <button type="button" onClick={() => setQuery('')} className="text-gray-400 hover:text-gray-600">
              <X className="h-3 w-3" />
            </button>
          )}
        </div>

        {/* Options list */}
        <div className="overflow-y-auto flex-1">
          {filtered.length === 0 ? (
            <p className="px-3 py-3 text-sm text-gray-400 text-center">No results found</p>
          ) : (
            filtered.map(opt => (
              <button
                key={opt}
                type="button"
                onClick={() => handleSelect(opt)}
                className={`
                  w-full text-left px-3 py-2 text-sm transition-colors truncate
                  ${opt === value
                    ? 'bg-[#6366F1] text-white font-medium'
                    : 'text-[#1E293B] hover:bg-indigo-50 hover:text-[#6366F1]'}
                `}
              >
                {opt}
              </button>
            ))
          )}
        </div>

        {/* Count hint */}
        {options.length > 10 && (
          <div className="px-3 py-1.5 border-t border-gray-50 bg-gray-50 flex-shrink-0">
            <p className="text-xs text-gray-400">
              {filtered.length} of {options.length} shown
            </p>
          </div>
        )}
      </Floating>
    </div>
  );
}
